import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { MessageCircle, Globe, Share2, BookOpen, ClipboardList, BotOff, Smartphone, CreditCard, Lock, Mail, ArrowRight } from "lucide-react";
import ProtectedEmail from "@/components/ProtectedEmail";

const ARTICLES = [
  { slug: "whatsapp", icon: MessageCircle, title: "Connect your agent to WhatsApp", blurb: "Sandbox in 5 minutes, then go live with a real WhatsApp Business number." },
  { slug: "meta-channels", icon: MessageCircle, title: "Connect Messenger, Instagram & WhatsApp Cloud", blurb: "Three Meta channels — one webhook. Step-by-step setup with screenshots." },
  { slug: "website-widget", icon: Globe, title: "Embed the chat widget on your website", blurb: "One-line snippet for any site — Shopify, WordPress, Webflow, plain HTML." },
  { slug: "share-link", icon: Share2, title: "Share a public chat link", blurb: "QR codes, link-in-bio, email signatures, social profiles." },
  { slug: "knowledge-base", icon: BookOpen, title: "Train your agent with documents", blurb: "Upload PDFs, CSVs, menus, FAQs — format tips for the best results." },
  { slug: "intake-form", icon: ClipboardList, title: "Capture leads with an intake form", blurb: "Configure questions, phone read-back, email & contact details." },
  { slug: "pause-ai", icon: BotOff, title: "Take over conversations manually", blurb: "Pause the AI, whitelist VIP contacts, handle hot leads yourself." },
  { slug: "custom-domain", icon: Smartphone, title: "Connect your custom domain", blurb: "DNS, CNAME, custom subdomain setup once you deploy." },
  { slug: "billing", icon: CreditCard, title: "Billing, plans & invoices", blurb: "Upgrade, change plan, cancel, download receipts." },
];

const LEGAL = [
  { slug: "privacy", icon: Lock, title: "Privacy Policy" },
  { slug: "terms", icon: Mail, title: "Terms of Service" },
  { slug: "cookies", icon: Mail, title: "Cookie Policy" },
  { slug: "gdpr", icon: Lock, title: "GDPR & Data Processing" },
];

export default function HelpIndex() {
  const { user } = useAuth({ redirectIfUnauthed: false });
  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Navbar user={user} />
      <div className="mx-auto max-w-5xl px-6 md:px-12 py-16 md:py-24">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Help center</div>
        <h1 className="font-serif text-4xl sm:text-6xl mb-6">Guides for every step.</h1>
        <p className="font-mono text-sm text-[#A39A8F] mb-16 max-w-2xl leading-relaxed">
          From your first agent to your hundredth customer — short, no-fluff guides that show exactly what to click.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-20" data-testid="help-articles-grid">
          {ARTICLES.map((a) => {
            const Icon = a.icon;
            return (
              <Link key={a.slug} to={`/help/${a.slug}`} data-testid={`help-card-${a.slug}`}
                className="group bg-[#1A1715] border border-[#F5F2EB]/10 p-6 hover:border-[#D95B38]/50 transition-colors flex flex-col">
                <Icon className="w-6 h-6 text-[#D95B38] mb-3" strokeWidth={1.5} />
                <div className="font-serif text-xl mb-2 group-hover:text-[#D95B38] transition-colors">{a.title}</div>
                <div className="font-mono text-xs text-[#A39A8F] mb-4 flex-1">{a.blurb}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] group-hover:text-[#D95B38] flex items-center gap-2">
                  Read guide <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            );
          })}
        </div>

        <div className="border-t border-[#F5F2EB]/10 pt-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-6">Legal & privacy</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="help-legal-grid">
            {LEGAL.map((l) => (
              <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={`help-legal-${l.slug}`}
                className="bg-[#1A1715] border border-[#F5F2EB]/10 p-4 hover:border-[#F5F2EB]/30 transition-colors font-mono text-xs text-[#E0D8CA] flex items-center gap-3">
                <l.icon className="w-4 h-4 text-[#A39A8F]" strokeWidth={1.5} />
                {l.title}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10 text-center">
          <Mail className="w-7 h-7 text-[#D95B38] mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl mb-3">Still stuck?</h3>
          <p className="font-mono text-sm text-[#A39A8F] mb-5 max-w-md mx-auto">
            Email <ProtectedEmail /> — we usually reply within a few hours.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
